import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { SERVICE_LOOKUP } from 'src/environments/global.config';

export interface DciCustomizationConfig {
	ipam: {
		enabled: boolean;
		allowOverride: boolean;
	};
}

@Injectable({
	providedIn: 'root',
})
export class ConfigManagerService {
	api: string = environment.api.configManager;

	constructor(
		private http: HttpClient,
		private userService: UserService
	) {
		const value = SERVICE_LOOKUP('configManager');
		if (value != null && value.endpoint !== this.api) {
			this.api = value.endpoint;
		}
	}

	/**
	 * Get configuration from configuration manager for the current tenant
	 * @param key The configuration key to query from the configuration manager
	 * @param fallback Fallback value to return if the configuration manager does not return the value. If left undefined, Error is thrown.
	 * @param useCache Whether to use sessionStorage cache or not. Cleared after the browser is closed.
	 * @returns Configuration object requested from the API or fallback.
	 * @throws Error if no configuration for the given key is found and no fallback is provided.
	 */
	async getConfig<T = any>(key: string, fallback?: T, useCache: boolean = true): Promise<T> {
		let cacheKey = '';

		if (useCache) {
			await this.userService.getUserInfo();
			const tenant = this.userService.currentTenant;
			cacheKey = `cache.${key}.${tenant}`;

			const cached = sessionStorage.getItem(cacheKey);
			if (cached) {
				return JSON.parse(cached);
			}
		}

		let value: T;
		try {
			value = await this.http.get<T>(`${this.api}/v1/Configs/${key}`).toPromise();
		} catch {
			if (fallback) {
				return fallback;
			}

			throw Error('Could not load configuration from path');
		}

		if (useCache) {
			sessionStorage.setItem(cacheKey, JSON.stringify(value));
		}

		return value;
	}
}
